import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { BrandId, Channel, ContentType, Status } from '@hub/shared'
import { request } from './api.js'

/** Mirrors `present()` / `getItem()` in apps/api/src/items/service.ts. */
export interface Item {
  id: string
  brand: BrandId
  title: string
  type: ContentType
  status: Status
  start: string
  end: string | null
  eventDate: string | null
  channels: Channel[]
  path: string
  note: string
  archived: boolean
  updatedAt: string
  updatedBy: 'lucas' | 'claude'
}

export type LinkKind = 'brevo_campaign' | 'upload_post_job' | 'social_post' | 'file' | 'url'

export interface ItemDetail extends Item {
  links: { id: string; kind: LinkKind; ref: string; label: string; metrics: Record<string, number> | null; metricsAt: string | null }[]
  activity: { actor: 'lucas' | 'claude'; action: string; diff: unknown; at: string }[]
}

export interface ItemQuery {
  brand?: string[]
  type?: string[]
  channel?: string[]
  status?: string[]
  from?: string
  to?: string
  q?: string
}

function queryString(q: ItemQuery): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(q)) {
    if (Array.isArray(value) ? value.length > 0 : value) params.set(key, Array.isArray(value) ? value.join(',') : value)
  }
  const s = params.toString()
  return s ? `?${s}` : ''
}

export function useItems(q: ItemQuery) {
  return useQuery({
    queryKey: ['items', q],
    queryFn: () => request<{ items: Item[] }>(`/items${queryString(q)}`).then((r) => r.items),
    placeholderData: (previous) => previous,
  })
}

export function useItem(id: string | undefined) {
  return useQuery({
    queryKey: ['item', id],
    queryFn: () => request<ItemDetail>(`/items/${id}`),
    enabled: id !== undefined,
  })
}

/** After any write: the detail gets the server's answer, every list refetches. */
function useSync() {
  const client = useQueryClient()
  return (detail: ItemDetail): void => {
    client.setQueryData(['item', detail.id], detail)
    void client.invalidateQueries({ queryKey: ['items'] })
  }
}

export type NewItem = Pick<Item, 'brand' | 'title' | 'type' | 'start' | 'channels'> &
  Partial<Pick<Item, 'status' | 'note' | 'path' | 'eventDate' | 'end'>>

export function useCreateItem() {
  const sync = useSync()
  return useMutation({
    mutationFn: (body: NewItem) => request<ItemDetail>('/items', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: sync,
  })
}

export type ItemPatch = Partial<Pick<Item, 'brand' | 'title' | 'type' | 'status' | 'start' | 'end' | 'eventDate' | 'channels' | 'path' | 'note'>>

export function useUpdateItem() {
  const client = useQueryClient()
  const sync = useSync()
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ItemPatch }) =>
      request<ItemDetail>(`/items/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    // Optimistic: a card dropped into a column must land there immediately.
    onMutate: async ({ id, patch }) => {
      await client.cancelQueries({ queryKey: ['items'] })
      const lists = client.getQueriesData<Item[]>({ queryKey: ['items'] })
      for (const [key, data] of lists) {
        if (data) client.setQueryData(key, data.map((i) => (i.id === id ? { ...i, ...patch } : i)))
      }
      return { lists }
    },
    onError: (_error, _vars, context) => {
      for (const [key, data] of context?.lists ?? []) client.setQueryData(key, data)
    },
    onSuccess: sync,
  })
}

export function useArchiveItem() {
  const sync = useSync()
  return useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      request<ItemDetail>(`/items/${id}/archive`, { method: 'POST', body: JSON.stringify({ archived }) }),
    onSuccess: sync,
  })
}

export function useAddLink() {
  const sync = useSync()
  return useMutation({
    mutationFn: ({ itemId, kind, ref, label }: { itemId: string; kind: LinkKind; ref: string; label?: string }) =>
      request<ItemDetail>(`/items/${itemId}/links`, { method: 'POST', body: JSON.stringify({ kind, ref, label: label ?? '' }) }),
    onSuccess: sync,
  })
}

export function useRemoveLink() {
  const sync = useSync()
  return useMutation({
    mutationFn: (linkId: string) => request<ItemDetail>(`/links/${linkId}`, { method: 'DELETE' }),
    onSuccess: sync,
  })
}

export interface FileRow {
  id: string
  name: string
  mimeType: string
  size: number
  webUrl: string
  modifiedAt: string
  thumb: string | null
}

export function useFiles(itemId: string) {
  return useQuery({
    queryKey: ['files', itemId],
    queryFn: () => request<{ configured: boolean; files: FileRow[] }>(`/items/${itemId}/files`),
  })
}

export interface HubStatus {
  connector: boolean
  connectorUrl: string | null
  files: boolean
}

export function useStatus() {
  return useQuery({ queryKey: ['status'], queryFn: () => request<HubStatus>('/status'), staleTime: 5 * 60_000 })
}
